// Sidebar — desktop fixed left rail + mobile slide-in drawer.
// Contains the logo, main navigation, and the user/logout footer.
import React from 'react';
import { Link } from 'react-router-dom';
import { X, LogOut, Sun, Moon } from 'lucide-react';
import { Button } from '../../ui/button';
import { useTheme } from '../../../contexts/ThemeContext';
import { MorningEmailToggle } from './MorningEmailToggle';
import { ChangePasswordButton } from './ChangePasswordButton';
import { useAuth } from '../../../contexts/AuthContext';
import { passesAccentContrast } from '../../../lib/tenantTheme';
import NotificationBell from '../../NotificationBell';

// Default Yabbit landscape lockup — used when the tenant hasn't uploaded
// their own logo. Kept as a hard-coded fallback so a fresh domain
// renders pixel-identical to the pre-theming build.
const DEFAULT_LOGO_URL = 'https://customer-assets.emergentagent.com/job_call-quality-grade/artifacts/ehb854bc_1.Landscape.png';

const NavItem = ({ item, isActive, onNavigate, accentSafe }) => {
  const Icon = item.icon;
  // Active-item treatment: the tenant's `--brand-primary` fill with
  // `--brand-primary-text` as the writing on top (TF: black button +
  // gold text). Falls back to the previous accent-fill treatment for
  // tenants that haven't set a primary. The `accentSafe` gate is kept
  // so nav never becomes unreadable if the accent flunks 3:1.
  const activeStyle = isActive
    ? {
        backgroundColor: 'var(--brand-primary, var(--brand-accent))',
        color: 'var(--brand-primary-text, var(--brand-accent-text, #fff))',
      }
    : undefined;
  return (
    <Link
      to={item.href}
      data-testid={`nav-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      style={activeStyle}
      className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all ${
        isActive && !accentSafe
          ? 'nav-item-active-branded bg-primary text-white shadow-sm'
          : isActive
            ? 'nav-item-active-branded shadow-sm'
            : 'text-slate-600 hover:bg-slate-100'
      }`}
      onClick={onNavigate}
    >
      <Icon className="w-5 h-5 mr-3" />
      {item.name}
      {item.badge > 0 && (
        <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-teal-500 text-white text-[11px] font-bold flex items-center justify-center"
          data-testid={`nav-badge-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
          {item.badge}
        </span>
      )}
    </Link>
  );
};

const SidebarHeader = ({ onClose }) => {
  const { user } = useAuth();
  // Read the brand vars off documentElement to know if the tenant has
  // uploaded logo assets — pull the actual URLs by re-reading the same
  // /tenant/branding call would be overkill; instead we mirror the URLs
  // onto CSS vars in the theme applier when they're set. Simpler: fetch
  // the URLs directly from the user's domain if the field is present.
  // Until the URLs land on the user object, use two additional data
  // attributes on <html> that AuthContext sets alongside the CSS vars.
  const iconUrl = document.documentElement.dataset.brandIconUrl || '';
  const logoUrl = document.documentElement.dataset.brandLogoUrl || DEFAULT_LOGO_URL;
  const isDefault = !document.documentElement.dataset.brandLogoUrl;
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
      <div className="flex items-center gap-3 min-w-0">
        {iconUrl ? (
          <img src={iconUrl} alt="" className="h-10 w-10 object-contain shrink-0" data-testid="sidebar-icon" />
        ) : null}
        <div className="min-w-0">
          <img
            src={logoUrl}
            alt={user?.name ? `${user.name}'s workspace` : 'Yabbit VoiceFabric'}
            className="h-10 w-auto object-contain max-w-[160px]"
            data-testid="sidebar-logo"
          />
          {/* Fixed, non-tenant-configurable credit — item 4 in the spec. */}
          {!isDefault && (
            <p className="mt-0.5 text-[10px] uppercase tracking-widest text-slate-400 font-medium leading-none" data-testid="sidebar-powered-by">
              Powered by Yabbit VoiceFabric
            </p>
          )}
        </div>
      </div>
      <button className="lg:hidden" onClick={onClose} data-testid="close-sidebar-btn">
        <X className="w-5 h-5 text-slate-600" />
      </button>
    </div>
  );
};

const UserFooter = ({ user, onLogout }) => {
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === 'light';  return (
    <div className="px-4 py-4 border-t border-slate-200 shrink-0">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-semibold">
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-slate-900">{user?.name}</p>
            <p className="text-xs text-slate-500">{user?.role}</p>
          </div>
        </div>
        <NotificationBell compact />
      </div>
      <Button
        variant="ghost"
        className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-100 mb-1"
        onClick={toggleTheme}
        data-testid="theme-toggle-tenant"
        title={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
      >
        {isLight ? <Sun className="w-4 h-4 mr-2 text-amber-500" /> : <Moon className="w-4 h-4 mr-2 text-slate-500" />}
        {isLight ? 'Light mode' : 'Dark mode'}
        <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-400">
          {isLight ? 'switch to dark' : 'switch to light'}
        </span>
      </Button>
      <MorningEmailToggle />
      <ChangePasswordButton />
      <Button
        variant="ghost"
        className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
        onClick={onLogout}
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Logout
      </Button>
    </div>
  );
};

const MANAGER_ROLES = new Set(['master_admin', 'domain_admin', 'domain_supervisor', 'manager', 'supervisor']);

export const Sidebar = ({ user, sections, currentPath, open, onClose, onLogout }) => {
  // Runtime guard: only apply the tenant accent to the active nav item
  // if a brand accent is set AND it passes 3:1 vs white text. Read from
  // the same CSS var the theme applier writes.
  const accentValue = typeof document !== 'undefined'
    ? getComputedStyle(document.documentElement).getPropertyValue('--brand-accent').trim()
    : '';
  const accentSafe = !!accentValue && passesAccentContrast(accentValue);
  return (
  <div
    className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
      open ? 'translate-x-0' : '-translate-x-full'
    }`}
  >
    <div className="flex flex-col h-full">
      <SidebarHeader onClose={onClose} />
      <nav className="flex-1 min-h-0 overflow-y-auto px-4 py-5 space-y-4">
        {sections.map((sec) => {
          const items = sec.items.filter((item) => (!item.managerOnly || MANAGER_ROLES.has(user?.role))
            && (!item.agentOnly || !MANAGER_ROLES.has(user?.role)));
          if (!items.length) return null;
          return (
            <div key={sec.section || 'classic'}>
              {sec.section && (
                <p
                  className="px-4 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400"
                  data-testid={`nav-section-${sec.section.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {sec.section}
                </p>
              )}
              <div className="space-y-1">
                {items.map(item => (
                  <NavItem
                    key={item.name}
                    item={item}
                    isActive={currentPath === item.href || (item.href === '/my-domain' && ['/', '/dashboard'].includes(currentPath))}
                    onNavigate={onClose}
                    accentSafe={accentSafe}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <UserFooter user={user} onLogout={onLogout} />
    </div>
  </div>
  );
};
